import { CheckCircle, OpenInNew, Schedule } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

interface MedicationTaskCardProps {
  medicationName: string;
  status: "pending" | "completed";
  dueTime?: string;
  completedAt?: string;
  onClick?: () => void;
}

export default function MedicationTaskCard({
  medicationName,
  status,
  dueTime,
  completedAt,
  onClick,
}: MedicationTaskCardProps) {
  const isCompleted = status === "completed";

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 1.5,
        py: 1,
        // maxWidth: 600,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: isCompleted
          ? alpha("#4caf50", 0.08)
          : alpha("#ff9800", 0.08),
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s ease",
        "&:hover": onClick
          ? {
              backgroundColor: isCompleted
                ? alpha("#4caf50", 0.12)
                : alpha("#ff9800", 0.12),
              borderColor: isCompleted ? "#4caf50" : "#ff9800",
            }
          : {},
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "6px",
          backgroundColor: isCompleted
            ? alpha("#4caf50", 0.15)
            : alpha("#ff9800", 0.15),
          color: isCompleted ? "#4caf50" : "#ff9800",
        }}
      >
        {isCompleted ? (
          <CheckCircle sx={{ fontSize: 28 }} />
        ) : (
          <Schedule sx={{ fontSize: 28 }} />
        )}
      </Box>

      {/* Content */}
      <Box flex={1} minWidth={0}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {medicationName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  padding: "0 0 0 0.5rem",
                  flexShrink: 0,
                }}
              >
                {isCompleted ? "Completed" : "Pending"}
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.75rem",
              }}
            >
              {isCompleted
                ? completedAt
                  ? `Completed ${new Date(completedAt).toLocaleString()}`
                  : "Task completed"
                : dueTime
                  ? `Due ${new Date(dueTime).toLocaleString()}`
                  : "Task pending"}
            </Typography>
          </Box>
          {onClick && (
            <OpenInNew
              sx={{
                fontSize: 18,
                color: "text.secondary",
                opacity: 0.6,
                flexShrink: 0,
              }}
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
}
